import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Trash2, GripVertical, WifiOff } from 'lucide-react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import api from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
const fmt = n => (n || 0).toLocaleString('ko-KR');

export default function EventItemsTab({ eventId }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const me = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const token = localStorage.getItem('paper_work_token');
  const isSuperAdmin = me?.role === 'superadmin';

  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);

  const [editingItemId, setEditingItemId] = useState(null);
  const [editingLocId, setEditingLocId] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemSpec, setNewItemSpec] = useState('');
  const [newLocName, setNewLocName] = useState('');
  const [showNewItem, setShowNewItem] = useState(false);
  const [showNewLoc, setShowNewLoc] = useState(false);

  // 행 드래그 = 품목, 열 드래그 = 장소
  const [dragRowItem, setDragRowItem] = useState(null);
  const [dragOverRowItem, setDragOverRowItem] = useState(null);
  const [dragColLoc, setDragColLoc] = useState(null);
  const [dragOverColLoc, setDragOverColLoc] = useState(null);

  // ─── Yjs ─────────────────────────────────────────────
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({});
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');
  const saveTimerRef = useRef({});

  const wsUrl = useMemo(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/yjs`;
  }, []);

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider(wsUrl, `event-items-${eventId}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/event-manage/projects/${eventId}/items-table`);
      setItems(data.items || []);
      setLocations(data.locations || []);
      const qMap = {};
      (data.quantities || []).forEach(q => { qMap[`${q.eventItemId}_${q.eventItemLocationId}`] = q.quantity || 0; });
      setQuantities(qMap);
    } catch {}
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);
    provider.awareness.setLocalStateField('user', {
      name: me?.name || me?.username || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const onAwareness = () => {
      const myName = me?.name || me?.username || 'Unknown';
      const seen = new Set(); const users = []; const cells = {};
      provider.awareness.getStates().forEach(state => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) { seen.add(state.user.name); users.push({ ...state.user, isMe: state.user.name === myName }); }
        if (state.user.editingCell && state.user.name !== myName) cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
      });
      const pStr = JSON.stringify(users); const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', onAwareness);
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };
  const setEditingCell = (cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  };

  // ─── CRUD ─────────────────────────────────────────────
  const addItem = async () => {
    if (!newItemName.trim()) return;
    try {
      const { data } = await api.post(`/event-manage/projects/${eventId}/items-table/items`, { name: newItemName, spec: newItemSpec, order: items.length });
      setItems(p => [...p, data]);
      setNewItemName(''); setNewItemSpec(''); setShowNewItem(false); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const deleteItem = async (id) => {
    try {
      await api.delete(`/event-manage/items-table/items/${id}`);
      setItems(p => p.filter(it => it.id !== id)); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const updateItem = async (id, fields) => {
    try { await api.put(`/event-manage/items-table/items/${id}`, fields); notifyChange(); }
    catch { alert('오류가 발생했습니다.'); }
  };

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    try {
      const { data } = await api.post(`/event-manage/projects/${eventId}/items-table/locations`, { name: newLocName, order: locations.length });
      setLocations(p => [...p, data]);
      setNewLocName(''); setShowNewLoc(false); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const deleteLocation = async (id) => {
    try {
      await api.delete(`/event-manage/items-table/locations/${id}`);
      setLocations(p => p.filter(l => l.id !== id)); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const updateLocation = async (id, fields) => {
    try { await api.put(`/event-manage/items-table/locations/${id}`, fields); notifyChange(); }
    catch { alert('오류가 발생했습니다.'); }
  };

  const setQty = (itemId, locId, val) => {
    const key = `${itemId}_${locId}`;
    setQuantities(p => ({ ...p, [key]: val }));
    if (saveTimerRef.current[key]) clearTimeout(saveTimerRef.current[key]);
    saveTimerRef.current[key] = setTimeout(() => {
      api.put('/event-manage/items-table/quantity', { eventItemId: itemId, eventItemLocationId: locId, quantity: val })
        .then(() => notifyChange());
    }, 600);
  };

  const getQty   = (itemId, locId) => quantities[`${itemId}_${locId}`] || 0;
  const rowTotal = (itemId) => locations.reduce((s, l) => s + getQty(itemId, l.id), 0);   // 품목별 합계
  const colTotal = (locId)  => items.reduce((s, it) => s + getQty(it.id, locId), 0);       // 장소별 합계
  const grandTotal = () => items.reduce((s, it) => s + rowTotal(it.id), 0);

  // 행 드래그 (품목 순서)
  const onRowDragStart = (itemId) => setDragRowItem(itemId);
  const onRowDragOver  = (e, itemId) => { e.preventDefault(); setDragOverRowItem(itemId); };
  const onRowDrop = (targetId) => {
    if (!dragRowItem || dragRowItem === targetId) return;
    const arr = [...items];
    const from = arr.findIndex(it => it.id === dragRowItem);
    const to   = arr.findIndex(it => it.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1); arr.splice(to, 0, moved);
    setItems(arr.map((it, i) => ({ ...it, order: i })));
    api.put('/event-manage/items-table/reorder-items', arr.map((it, i) => ({ id: it.id, order: i }))).then(() => notifyChange());
    setDragRowItem(null); setDragOverRowItem(null);
  };

  // 열 드래그 (장소 순서)
  const onColDragStart = (locId) => setDragColLoc(locId);
  const onColDragOver  = (e, locId) => { e.preventDefault(); setDragOverColLoc(locId); };
  const onColDrop = (targetId) => {
    if (!dragColLoc || dragColLoc === targetId) return;
    const arr = [...locations];
    const from = arr.findIndex(l => l.id === dragColLoc);
    const to   = arr.findIndex(l => l.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1); arr.splice(to, 0, moved);
    setLocations(arr.map((l, i) => ({ ...l, order: i })));
    api.put('/event-manage/items-table/reorder-locations', arr.map((l, i) => ({ id: l.id, order: i }))).then(() => notifyChange());
    setDragColLoc(null); setDragOverColLoc(null);
  };

  const bdr = isDark ? 'border-white/10' : 'border-gray-200';
  const cellCls = `border ${bdr} px-2 py-1.5 text-xs`;
  const hCls = `${cellCls} whitespace-nowrap text-center font-medium py-2 ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-50 text-gray-500'}`;

  if (loading) return <div className={`py-20 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>로딩 중...</div>;

  return (
    <div>
      {/* 상단 툴바 */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm">
          <span className={isDark ? 'text-white/40' : 'text-gray-400'}>총 수량</span>
          <span className={`font-semibold ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(grandTotal())}개</span>
          <span className={`ml-3 text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{items.length}개 품목 · {locations.length}개 장소</span>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <>
              <button onClick={() => setShowNewItem(p => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                <Plus size={12} /> 품목
              </button>
              <button onClick={() => setShowNewLoc(p => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                <Plus size={12} /> 장소
              </button>
            </>
          )}
          <div className="flex items-center gap-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white overflow-hidden border-2 ${p.isMe ? 'border-brand' : 'border-transparent'}`}
                style={{ backgroundColor: p.color }} title={p.name}>
                {p.name?.charAt(0)}
              </div>
            ))}
            {!connected && <WifiOff size={13} className="text-red-400" />}
          </div>
        </div>
      </div>

      {/* 새 품목 입력 */}
      {showNewItem && (
        <div className={`flex items-center gap-2 mb-3 p-3 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
          <input value={newItemName} onChange={e => setNewItemName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()}
            placeholder="품목명" className={`flex-1 border rounded px-2 py-1.5 text-sm outline-none ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`} autoFocus />
          <input value={newItemSpec} onChange={e => setNewItemSpec(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()}
            placeholder="사양 (선택)" className={`w-40 border rounded px-2 py-1.5 text-sm outline-none ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
          <button onClick={addItem} className="px-3 py-1.5 bg-brand hover:bg-brand/90 text-white text-xs rounded">추가</button>
          <button onClick={() => setShowNewItem(false)} className={`px-3 py-1.5 text-xs rounded ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>취소</button>
        </div>
      )}
      {showNewLoc && (
        <div className={`flex items-center gap-2 mb-3 p-3 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
          <input value={newLocName} onChange={e => setNewLocName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addLocation()}
            placeholder="장소명" className={`flex-1 border rounded px-2 py-1.5 text-sm outline-none ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`} autoFocus />
          <button onClick={addLocation} className="px-3 py-1.5 bg-brand hover:bg-brand/90 text-white text-xs rounded">추가</button>
          <button onClick={() => setShowNewLoc(false)} className={`px-3 py-1.5 text-xs rounded ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>취소</button>
        </div>
      )}

      {/* 매트릭스 테이블: 행=품목, 열=장소 */}
      <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent">
          <table className="text-xs border-collapse w-full" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {isSuperAdmin && <col style={{ width: '28px' }} />}
              <col style={{ width: '120px' }} />   {/* 품목명: 고정 */}
              <col style={{ width: '72px' }} />    {/* 사양: 고정 */}
              {locations.map(l => <col key={l.id} />)}  {/* 남은 공간 균등 분배 */}
              <col style={{ width: '60px' }} />    {/* 합계: 고정 */}
            </colgroup>
            <thead>
              <tr>
                {/* 왼쪽 고정 헤더 */}
                {isSuperAdmin && (
                  <th className={`${hCls} w-7`}></th>
                )}
                <th className={`${hCls} text-left pl-3`}>품목명</th>
                <th className={hCls}>사양</th>

                {/* 장소 열 헤더 (드래그 가능) */}
                {locations.map(loc => (
                  <th key={loc.id}
                    draggable={isSuperAdmin}
                    onDragStart={isSuperAdmin ? () => onColDragStart(loc.id) : undefined}
                    onDragOver={isSuperAdmin ? e => onColDragOver(e, loc.id) : undefined}
                    onDrop={isSuperAdmin ? () => onColDrop(loc.id) : undefined}
                    onDragEnd={() => { setDragColLoc(null); setDragOverColLoc(null); }}
                    className={`${hCls} group transition-colors
                      ${dragColLoc === loc.id ? 'opacity-40' : ''}
                      ${dragOverColLoc === loc.id ? (isDark ? 'bg-brand/20' : 'bg-brand/10') : ''}`}>
                    {/* 이름 행: grip + 이름 + 삭제 */}
                    <div className="flex items-center justify-center gap-0.5 px-1">
                      {isSuperAdmin && (
                        <GripVertical size={10} className={`cursor-grab shrink-0 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                      )}
                      {editingLocId === loc.id ? (
                        <input defaultValue={loc.name} autoFocus
                          onBlur={e => { updateLocation(loc.id, { name: e.target.value }); setEditingLocId(null); }}
                          onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                          className={`flex-1 min-w-0 text-xs text-center bg-transparent outline-none border-b ${isDark ? 'text-white border-white/30' : 'text-gray-900 border-gray-400'}`} />
                      ) : (
                        <span onClick={() => isSuperAdmin && setEditingLocId(loc.id)}
                          className={`flex-1 truncate cursor-pointer ${isDark ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}>
                          {loc.name}
                        </span>
                      )}
                      {isSuperAdmin && (
                        <button onClick={() => deleteLocation(loc.id)}
                          className={`shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-white/30 hover:text-red-400' : 'text-gray-300 hover:text-red-400'}`}>
                          <Trash2 size={10} />
                        </button>
                      )}
                    </div>
                    {/* 수량 */}
                    <div className="text-brand font-semibold text-[11px] mt-0.5">{fmt(colTotal(loc.id))}</div>
                  </th>
                ))}
                <th className={hCls}>합계</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={locations.length + (isSuperAdmin ? 4 : 3)} className={`py-10 text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                    품목과 장소를 추가하세요.
                  </td>
                </tr>
              ) : items.map(it => {
                const isDragging = dragRowItem === it.id;
                const isDragOver = dragOverRowItem === it.id;
                const total = rowTotal(it.id);
                return (
                  <tr key={it.id}
                    draggable={isSuperAdmin}
                    onDragStart={isSuperAdmin ? () => onRowDragStart(it.id) : undefined}
                    onDragOver={isSuperAdmin ? e => onRowDragOver(e, it.id) : undefined}
                    onDrop={isSuperAdmin ? () => onRowDrop(it.id) : undefined}
                    onDragEnd={() => { setDragRowItem(null); setDragOverRowItem(null); }}
                    className={`transition-colors
                      ${isDark ? 'hover:bg-white/3' : 'hover:bg-gray-50'}
                      ${isDragging ? 'opacity-40' : ''}
                      ${isDragOver ? 'border-t-2 border-brand' : ''}`}>
                    {/* Grip */}
                    {isSuperAdmin && (
                      <td className={`${cellCls} text-center ${isDark ? 'bg-white/3' : 'bg-gray-50/50'}`}>
                        <GripVertical size={11} className={`cursor-grab mx-auto ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                      </td>
                    )}
                    {/* 품목명 */}
                    <td className={`${cellCls} pl-3 ${isDark ? 'bg-white/3' : 'bg-gray-50/50'}`}>
                      <div className="flex items-center justify-between gap-1 overflow-hidden">
                        {editingItemId === it.id ? (
                          <input defaultValue={it.name} autoFocus
                            onBlur={e => { updateItem(it.id, { name: e.target.value }); setEditingItemId(null); }}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            className={`flex-1 text-xs bg-transparent outline-none border-b min-w-0 ${isDark ? 'text-white border-white/30' : 'text-gray-900 border-gray-400'}`} />
                        ) : (
                          <span onClick={() => isSuperAdmin && setEditingItemId(it.id)}
                            className={`font-medium truncate cursor-pointer ${isDark ? 'text-white' : 'text-gray-800'}`} title={it.name}>{it.name}</span>
                        )}
                        {isSuperAdmin && (
                          <button onClick={() => deleteItem(it.id)} className={`shrink-0 transition-colors ${isDark ? 'text-white/15 hover:text-red-400' : 'text-gray-300 hover:text-red-400'}`}><Trash2 size={11} /></button>
                        )}
                      </div>
                    </td>
                    {/* 사양 */}
                    <td className={`${cellCls} text-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {it.spec || '-'}
                    </td>
                    {/* 수량 셀 */}
                    {locations.map(loc => {
                      const key = `${it.id}_${loc.id}`;
                      const peer = peerCells[key];
                      const qty = quantities[key] ?? 0;
                      return (
                        <td key={loc.id} className={`${cellCls} text-center p-1`}
                          style={peer ? { boxShadow: `inset 0 0 0 2px ${peer.color}` } : {}}>
                          <input type="number" min="0" value={qty}
                            onChange={e => setQty(it.id, loc.id, Number(e.target.value) || 0)}
                            onFocus={() => setEditingCell(key)}
                            onBlur={() => setEditingCell(null)}
                            className={`w-full text-center text-xs bg-transparent outline-none rounded py-0.5 font-medium
                              ${qty > 0 ? 'text-brand' : isDark ? 'text-white/20' : 'text-gray-300'}
                              ${isDark ? 'focus:bg-white/5' : 'focus:bg-gray-100'}`} />
                        </td>
                      );
                    })}
                    {/* 행 합계 */}
                    <td className={`${cellCls} text-center font-semibold ${total > 0 ? 'text-brand' : isDark ? 'text-white/20' : 'text-gray-300'}`}>
                      {fmt(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* 열 합계 행 */}
            {items.length > 0 && locations.length > 0 && (
              <tfoot>
                <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                  {isSuperAdmin && <td className={cellCls}></td>}
                  <td className={`${cellCls} pl-3 font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`} colSpan={2}>장소별 합계</td>
                  {locations.map(loc => (
                    <td key={loc.id} className={`${cellCls} text-center font-semibold text-brand`}>{fmt(colTotal(loc.id))}</td>
                  ))}
                  <td className={`${cellCls} text-center font-bold text-brand`}>{fmt(grandTotal())}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
